import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import {
  SOLAR_KEY,
  PEDIDO_KEY,
  isAuthenticated,
  getClientData,
} from "../services/auth";
import { ClientData } from "../types/auth";

// ============================================
// Interface consolidada: App + Auth
// ============================================
interface AppContextData {
  // === Auth ===
  loggedIn: boolean;
  loading: boolean;
  error: string | null;
  clientData: ClientData | null;
  setLoggedIn: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  setError: (error: string | null) => void;
  setClientData: (data: ClientData | null) => void;

  // === Campanha Solar ===
  isCampanhaSolar: boolean;
  setCampanhaSolar: (status: boolean) => void;
  getCampanhaSolarValue: () => "S" | "N";

  // === Pedido Ativo ===
  nrPedidoAtivo: string | null;
  setNrPedidoAtivo: (nrPedido: string | null) => void;
}

const AppContext = createContext<AppContextData>({} as AppContextData);

// ============================================
// Hook unificado
// ============================================
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

// Alias para compatibilidade com código existente que usa useAuth
export const useAuth = useAppContext;

// ============================================
// Provider consolidado
// ============================================
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  // === Auth State ===
  const [loggedIn, setLoggedInState] = useState<boolean>(false);
  const [loading, setLoadingState] = useState<boolean>(true);
  const [error, setErrorState] = useState<string | null>(null);
  const [clientData, setClientDataState] = useState<ClientData | null>(null);

  // === Campanha Solar State ===
  const getCampanhaSolarFromStorage = useCallback(() => {
    const value = localStorage.getItem(SOLAR_KEY);
    return value !== null && value !== "false";
  }, []);

  const [isCampanhaSolar, setIsCampanhaSolarState] = useState<boolean>(
    getCampanhaSolarFromStorage
  );

  // === Pedido Ativo State ===
  const getNrPedidoAtivoFromStorage = useCallback(() => {
    return localStorage.getItem(PEDIDO_KEY);
  }, []);

  const [nrPedidoAtivo, setNrPedidoAtivoState] = useState<string | null>(
    getNrPedidoAtivoFromStorage
  );

  // ============================================
  // Effects
  // ============================================

  // Sincroniza Auth com localStorage na inicialização
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      const data = authenticated ? getClientData() : null;
      setLoggedInState(authenticated);
      setClientDataState(data);
      setLoadingState(false);
    };

    checkAuth();

    const handleStorageChange = (e: StorageEvent) => {
      // Auth
      if (
        e.key === null ||
        e.key?.includes("token") ||
        e.key?.includes("client")
      ) {
        checkAuth();
      }
      // Campanha Solar
      if (e.key === SOLAR_KEY) {
        setIsCampanhaSolarState(getCampanhaSolarFromStorage());
      }
      // Pedido Ativo
      if (e.key === PEDIDO_KEY) {
        setNrPedidoAtivoState(getNrPedidoAtivoFromStorage());
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [getCampanhaSolarFromStorage, getNrPedidoAtivoFromStorage]);

  // ============================================
  // Auth Actions
  // ============================================
  const setLoggedIn = useCallback((value: boolean) => {
    setLoggedInState(value);
  }, []);

  const setLoading = useCallback((value: boolean) => {
    setLoadingState(value);
  }, []);

  const setError = useCallback((errorValue: string | null) => {
    setErrorState(errorValue);
  }, []);

  const setClientData = useCallback((data: ClientData | null) => {
    setClientDataState(data);
    if (data) {
      const authenticated = isAuthenticated();
      if (authenticated) {
        setLoggedInState(true);
      }
    }
  }, []);

  // ============================================
  // Campanha Solar Actions
  // ============================================
  const setCampanhaSolar = useCallback((status: boolean) => {
    if (status) {
      localStorage.setItem(SOLAR_KEY, "true");
    } else {
      localStorage.removeItem(SOLAR_KEY);
    }
    setIsCampanhaSolarState(status);
  }, []);

  const getCampanhaSolarValue = useCallback((): "S" | "N" => {
    return isCampanhaSolar ? "S" : "N";
  }, [isCampanhaSolar]);

  // ============================================
  // Pedido Ativo Actions
  // ============================================
  const setNrPedidoAtivo = useCallback((nrPedido: string | null) => {
    if (nrPedido === null) {
      localStorage.removeItem(PEDIDO_KEY);
    } else {
      localStorage.setItem(PEDIDO_KEY, nrPedido);
    }
    setNrPedidoAtivoState(nrPedido);
  }, []);

  // ============================================
  // Provider
  // ============================================
  return (
    <AppContext.Provider
      value={{
        // Auth
        loggedIn,
        loading,
        error,
        clientData,
        setLoggedIn,
        setLoading,
        setError,
        setClientData,
        // Campanha Solar
        isCampanhaSolar,
        setCampanhaSolar,
        getCampanhaSolarValue,
        // Pedido Ativo
        nrPedidoAtivo,
        setNrPedidoAtivo,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// Re-export para compatibilidade
export { AppProvider as AuthProvider };
